import { z } from "zod";

// V2 Empty Bird Form
export const emptyBirdForm = {
  birdId: undefined,
  birdFlockId: undefined,
  birdActive: true,
  birdNumber: "",
  birdSpecies: undefined,
  birdAgeInWeeks: undefined,
  birdDateFirstBleed: "",
  birdDateTerminated: "",
  birdNote: "",
};

// V2 Empty Form
export const emptyFlockForm: FlockFormInput = {
  flockOrganizationId: 0,
  flockActive: true,
  flockName: "",
  flockNumber: "",
  flockStreetAddress: "",
  flockCity: "",
  flockStateId: 0,
  flockZip: "",
  flockCountyId: 0,
  flockIsAddressValidated: false,
  flockLatitude: "",
  flockLongitude: "",
  flockGpsNotes: "",
  flockComment: "",
  flockPrimaryContactFirstName: "",
  flockPrimaryContactLastName: "",
  flockPrimaryContactEmail: "",
  flockPrimaryContactPhoneNumber: "",
  flockSecondaryContactFirstName: "",
  flockSecondaryContactLastName: "",
  flockSecondaryContactEmail: "",
  flockSecondaryContactPhoneNumber: "",
  flockBirds: [{ ...emptyBirdForm }], // add an empty bird by default
};

// New v2 schemas for Sentinel Flocks and Birds

// Bird Schema
export const birdSchema = z.object({
  birdId: z.number().optional(), // Optional ID for existing records
  birdFlockId: z.number().optional(), // Parent reference
  birdActive: z.boolean().default(true),
  birdNumber: z.string().optional(),
  birdSpecies: z.number().optional(),
  birdAgeInWeeks: z.number().optional(),
  birdDateFirstBleed: z.string().optional(),
  birdDateTerminated: z.string().optional(),
  birdNote: z.string().optional(),
});

// Flock Schema
export const flockSchema = z.object({
  flockId: z.number().optional(), // Optional ID for existing records
  flockOrganizationId: z.number().min(1, "Organization is required"),
  flockActive: z.boolean().default(true),
  flockName: z.string().optional(),
  flockNumber: z.string().optional(),
  flockStreetAddress: z.string().optional(),
  flockCity: z.string().min(1, "City is required"),
  flockStateId: z.number().min(1, "State is required"),
  flockZip: z.string().min(1, "Zip is required"),
  flockCountyId: z.number().min(1, "County is required"),
  flockIsAddressValidated: z.boolean().default(false),
  flockLatitude: z.string().optional().nullable(),
  flockLongitude: z.string().optional().nullable(),
  flockGpsNotes: z.string().optional().nullable(),
  flockComment: z.string().optional().nullable(),
  flockPrimaryContactFirstName: z.string().optional(),
  flockPrimaryContactLastName: z.string().optional(),
  flockPrimaryContactEmail: z.email().optional().or(z.literal("")),
  flockPrimaryContactPhoneNumber: z.string().optional(),
  flockSecondaryContactFirstName: z.string().optional(),
  flockSecondaryContactLastName: z.string().optional(),
  flockSecondaryContactEmail: z.email().optional().or(z.literal("")),
  flockSecondaryContactPhoneNumber: z.string().optional(),
  flockBirds: z.array(birdSchema).default([]),
});

export type FlockFormInput = z.infer<typeof flockSchema>;
export interface FlockRecord extends FlockFormInput {
  // Display names from API for table views (from FlockSummary)
  organizationName?: string;
  countyName?: string;
  // Metadata
  createdBy?: string;
  updatedBy?: string;
  createdUtc: string;
  updatedUtc: string;
  birdCount?: number; // Total number of birds at this site
  _commentId?: number; // ID of the first comment (for updates) - internal use only
}

// === SENTINEL PACKING SLIP TYPES ===
// Match API response exactly - no transformations

// PackingSlipSummary from API - used for list view
export interface PackingSlipSummary {
  id: number;
  code: string;
  county: { id: number; name: string; text?: string };
  organization: { id: number; name: string; text?: string };
  dateCreated: string;
  createdByUser:
    | string
    | { displayName?: string; userName?: string; id?: number };
}

// PackingSlip from API - used for detail view
export interface PackingSlip {
  id: number;
  code: string;
  county: { id: number; name: string; text?: string };
  organization: { id: number; name: string; text?: string };
  datePacked: string | null;
  dateCreated: string;
  createdByUser:
    | string
    | { displayName?: string; userName?: string; id?: number };
  samples: PackingSlipSample[];
}

// PackingSlipSample from API
export interface PackingSlipSample {
  id: number;
  packingSlipId: number;
  code: string; // Barcode
  flockName: string;
  bird: {
    id: number;
    flockId: number;
    birdNumber: string;
    species: { id: number; text: string };
    ageInWeeks: number;
    dateOfFirstBleed: string;
    dateTerminated: string | null;
    note: string;
    active: boolean;
  };
  sampleIncluded: boolean;
  isNewBird: boolean;
  dateCollected: string;
}

// Bird from API (used for fetching active birds)
// Matches BirdSummary from API with embedded flock and organization data
export interface Bird {
  id: number;
  flockId: number;
  flockName: string;
  flockNumber: string;
  flockCounty: { id: number; name: string; code: string };
  organization: { id: number; text: string };
  birdNumber: string;
  species: { id: number; text: string };
  ageInWeeks: number;
  dateOfFirstBleed: string;
  dateTerminated: string | null;
  note: string;
  active: boolean;
}

// === INPUT TYPES FOR CREATING PACKING SLIPS ===
// Match API Input DTOs exactly

// AddPackingSlipSampleInput from API
export interface AddPackingSlipSampleInput {
  flockId: number;
  birdId: number;
  countyCode: string;
  sampleIncluded: boolean;
  isNewBird: boolean;
  dateCollected: string; // ISO string
}

// AddPackingSlipInput from API
export interface AddPackingSlipInput {
  countyId: number;
  organizationId: number;
  samples: AddPackingSlipSampleInput[];
}

// === INPUT TYPES FOR CREATING FLOCKS ===
// Match API Input DTOs exactly

// AddBirdInput from API - uses camelCase to match API JSON
export interface AddBirdInput {
  birdNumber: string;
  speciesId: number;
  ageInWeeks: number;
  dateOfFirstBleed: string; // ISO string
  dateTerminated: string | null; // ISO string
  note: string;
  active: boolean;
}

// UpdateFlockInput from API (base for AddFlockInput)
export interface UpdateFlockInput {
  name: string;
  organizationId: number;
  number: string;
  streetAddress: string;
  city: string;
  stateId: number;
  countyId: number;
  zip: string;
  latitude: string;
  longitude: string;
  isAddressValidated: boolean;
  gpsNote: string;
  active: boolean;
  primaryContactFirstName: string;
  primaryContactLastName: string;
  primaryContactPhoneNumber: string;
  primaryContactEmail: string;
  secondaryContactFirstName: string;
  secondaryContactLastName: string;
  secondaryContactPhoneNumber: string;
  secondaryContactEmail: string;
  comment: string;
}

// AddFlockInput from API
export interface AddFlockInput extends UpdateFlockInput {
  birds: AddBirdInput[];
}

// === UTILITY FUNCTIONS ===

export const generatePackingSlipCode = (
  countyId: number,
  date: Date = new Date()
): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const randomSixDigit = Math.floor(100000 + Math.random() * 900000);

  return `${countyId
    .toString()
    .padStart(2, "0")}-${year}-${month}-${day}-${randomSixDigit}`;
};

// === VALIDATION HELPERS ===

export const validatePackingSlipCode = (code: string): boolean => {
  // Validate format: countyID - YYYY-MM-DD - Random 6 digit number
  const codeRegex = /^\d{2}-\d{4}-\d{2}-\d{2}-\d{6}$/;
  return codeRegex.test(code);
};
